import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen w-full  text-left">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white  p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Privacy Policy of The Romanelli Group (TRG)</h1>
          
          <p className="text-sm text-gray-600 mb-8">LAST UPDATED: December 1, 2025</p>
          
          <div className="prose prose-lg max-w-none">
            <p className="mb-6">
              The Romanelli Group ("TRG," "we," "us," or "our") is committed to protecting your privacy and ensuring you understand how we collect, use, and disclose your information. This Privacy Policy explains our practices regarding information collected through:
            </p>
            
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Our website(s) operated by The Romanelli Group that link to this Privacy Policy ("TRG Website(s)"),</li>
              <li>Our digital tools, web-based applications, and online forms,</li>
              <li>Email newsletters or communications that link to this Privacy Policy,</li>
              <li>Information you provide directly to us via email, text, or phone, and</li>
              <li>Any offline interactions you have with TRG team members.</li>
            </ul>
            
            <p className="mb-6">
              Collectively, we refer to all of the above as the "Services."
            </p>
            
            <p className="mb-8">
              TRG is an independent real estate team operating under Keller Williams Classic Properties Realty. This Privacy Policy does not apply to the privacy practices of Keller Williams Realty corporate entities, KW® Market Centers, or other third-party real estate agents or brokers. For details about their privacy practices, please contact them directly.
            </p>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">SECTION 1: PERSONAL INFORMATION</h2>
            
            <p className="mb-4">
              "Personal Information" is information that identifies you or can be used to identify you. The Personal Information we collect depends on your relationship with TRG and how you use our Services.
            </p>
            
            <h3 className="text-xl font-bold text-gray-900 mb-4">Personal Information We May Collect From Consumers</h3>
            
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li><strong>Identifiers:</strong> name, email address, phone number, postal address, IP address.</li>
              <li><strong>Real estate–related information:</strong> buying and selling interests, home search preferences.</li>
              <li><strong>Financial information:</strong> mortgage status, budget range, or other financial details you choose to share.</li>
              <li><strong>Geolocation data:</strong> derived from your device or IP address.</li>
              <li><strong>Device information:</strong> browser, settings, or information you share from your device.</li>
              <li><strong>Account information:</strong> if you link third-party platforms (e.g., Google, Facebook).</li>
              <li><strong>Other information:</strong> that may be treated as Personal Information under applicable law.</li>
            </ul>
            
            <h3 className="text-xl font-bold text-gray-900 mb-4">How We Collect Personal Information</h3>
            
            <p className="mb-4">We and our service providers collect Personal Information in three ways:</p>
            
            <h4 className="text-lg font-semibold text-gray-900 mb-3">1. Directly from You</h4>
            <p className="mb-2">For example, when you:</p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Submit a contact form on the TRG Website,</li>
              <li>Request a home valuation or market report,</li>
              <li>Communicate with us via phone, text, or email,</li>
              <li>Attend an open house or event and voluntarily provide information.</li>
            </ul>
            
            <h4 className="text-lg font-semibold text-gray-900 mb-3">2. Automatically via the Services</h4>
            <p className="mb-2">Through tools such as:</p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Cookies,</li>
              <li>Pixel tags,</li>
              <li>Analytics technologies,</li>
              <li>Device/browser settings.</li>
            </ul>
            
            <h4 className="text-lg font-semibold text-gray-900 mb-3">3. From Other Sources</h4>
            <p className="mb-2">Including:</p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Public databases,</li>
              <li>Third-party marketing partners,</li>
              <li>Social media platforms,</li>
              <li>Keller Williams Market Centers if you interacted with them and requested follow-up from TRG.</li>
            </ul>
            
            <h3 className="text-xl font-bold text-gray-900 mb-4">How We Use Personal Information</h3>
            
            <p className="mb-4">We use Personal Information for legitimate business purposes, including:</p>
            
            <h4 className="text-lg font-semibold text-gray-900 mb-3">Providing Services & Fulfilling Requests</h4>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Creating or managing your account,</li>
              <li>Responding to your inquiries,</li>
              <li>Sending transactional or administrative updates,</li>
              <li>Helping you buy, sell, or evaluate real estate.</li>
            </ul>
            
            <h4 className="text-lg font-semibold text-gray-900 mb-3">Marketing & Communications</h4>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Sending newsletters, updates, or promotions,</li>
              <li>Sharing property alerts or market information,</li>
              <li>Facilitating optional social media interactions.</li>
            </ul>
            
            <h4 className="text-lg font-semibold text-gray-900 mb-3">Personalization & Analytics</h4>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Understanding your preferences,</li>
              <li>Improving our Services,</li>
              <li>Delivering content tailored to your interests.</li>
            </ul>
            
            <h4 className="text-lg font-semibold text-gray-900 mb-3">Sweepstakes or Promotions</h4>
            <p className="mb-4">If you participate, additional rules may apply.</p>
            
            <h4 className="text-lg font-semibold text-gray-900 mb-3">Aggregation & Anonymization</h4>
            <p className="mb-4">We may create and use de-identified information for internal improvements, analytics, and reporting.</p>
            
            <h4 className="text-lg font-semibold text-gray-900 mb-3">Business Operations</h4>
            <p className="mb-2">Including:</p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Auditing,</li>
              <li>Fraud/security monitoring,</li>
              <li>Improving products and services,</li>
              <li>Identifying usage trends,</li>
              <li>Measuring campaign effectiveness.</li>
            </ul>
            
            <h3 className="text-xl font-bold text-gray-900 mb-4">Disclosure of Personal Information</h3>
            
            <p className="mb-4">We may disclose your Personal Information to:</p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Trusted service providers (e.g., analytics, hosting providers, email systems),</li>
              <li>KW® Market Centers or KW® Agents if you request contact or information via TRG forms,</li>
              <li>Joint marketing partners (when legally permitted),</li>
              <li>Sweepstakes or promotion sponsors (if applicable),</li>
              <li>Other parties when required by law or necessary to protect rights, safety, or property.</li>
            </ul>
            
            <p className="mb-8 font-semibold">We do not sell Personal Information.</p>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">SECTION 2: OTHER INFORMATION</h2>
            
            <p className="mb-4">"Other Information" includes data that does not directly identify you, such as:</p>
            <ul className="list-disc pl-6 mb-8 space-y-1">
              <li>Device/browser information,</li>
              <li>App usage data,</li>
              <li>Cookies and tracking technologies,</li>
              <li>Demographic or statistical information,</li>
              <li>Aggregated data trends.</li>
            </ul>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">SECTION 3: SECURITY</h2>
            
            <p className="mb-8">
              TRG uses reasonable administrative, technical, and organizational safeguards to protect Personal Information. No system is 100% secure. If you suspect a security issue, contact us immediately.
            </p>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">SECTION 4: YOUR PRIVACY RIGHTS</h2>
            
            <p className="mb-4">Depending on your location, you may have rights such as:</p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Accessing your Personal Information,</li>
              <li>Correcting inaccurate information,</li>
              <li>Requesting deletion,</li>
              <li>Opting out of marketing communications,</li>
              <li>Requesting a copy of your data.</li>
            </ul>
            
            <p className="mb-4 font-semibold">How to submit a request:</p>
            <p className="mb-4">Email us at <a href="mailto:theromanelligroup@gmail.com" className="text-blue-600 hover:text-blue-800">theromanelligroup@gmail.com</a></p>
            
            <p className="mb-4">Your request must include:</p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Name,</li>
              <li>Address,</li>
              <li>Email or phone number,</li>
              <li>Type of privacy request.</li>
            </ul>
            
            <p className="mb-8">We will verify your identity before fulfilling the request.</p>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">SECTION 5: PHONE CALLS AND TEXT MESSAGES</h2>
            
            <p className="mb-6">
              By providing your phone number to TRG, you consent to receive calls or texts (including marketing messages) related to real estate services. You may opt out anytime by following instructions in the message or emailing us.
            </p>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">SECTION 6: RETENTION OF INFORMATION</h2>
            
            <p className="mb-4">We retain Personal Information based on:</p>
            <ul className="list-disc pl-6 mb-8 space-y-1">
              <li>The duration of our relationship,</li>
              <li>Legal obligations,</li>
              <li>Business needs,</li>
              <li>Security and compliance requirements.</li>
            </ul>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">SECTION 7: THIRD-PARTY SERVICES</h2>
            
            <p className="mb-4">TRG is not responsible for the privacy practices of:</p>
            <ul className="list-disc pl-6 mb-8 space-y-1">
              <li>KW® Market Centers,</li>
              <li>Other KW® Agents,</li>
              <li>Third-party websites,</li>
              <li>App providers such as Google, Apple, Meta/Instagram, etc.</li>
            </ul>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">SECTION 8: MINORS</h2>
            
            <p className="mb-8">
              TRG Services are not intended for individuals under 16. If you believe a minor's Personal Information was collected, contact us.
            </p>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">SECTION 9: INTERNATIONAL USERS</h2>
            
            <p className="mb-8">
              If you access TRG Services from outside the U.S., your data may be processed in the United States.
            </p>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">SECTION 10: CONTACTING US</h2>
            
            <p className="mb-4">For questions about this Privacy Policy or to submit a privacy-related request:</p>
            
            <div className="">
              <p className=""><strong>The Romanelli Group</strong></p>
              <p>Email: <a href="mailto:theromanelligroup@gmail.com" className="text-blue-600 hover:text-blue-800">theromanelligroup@gmail.com</a></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;